/** [ROLE] Thai/EN role labels — prefer authz matrix `roleLabels`, fallback to seed defaults. */

import { apiGet, API_BASE_URL } from "@/lib/ApiFetch";

export type AuthzRoleLabel = { th: string; en?: string };
export type AuthzRoleLabelsJson = Record<string, AuthzRoleLabel>;

/** Seed fallback when matrix has not loaded yet (matches market-api DEFAULT_ROLE_LABELS). */
export const FALLBACK_ROLE_LABELS: AuthzRoleLabelsJson = {
  owner: { th: "เจ้าของ", en: "Owner" },
  admin: { th: "แอดมิน", en: "Admin" },
  operator: { th: "พนักงาน", en: "Operator" },
  accountant: { th: "บัญชี", en: "Accountant" },
  submitter: { th: "ผู้ส่งเอกสาร", en: "Submitter" },
};

export const WORKSPACE_INVITE_STATUS_LABEL_TH: Record<string, string> = {
  pending: "รอรับเชิญ",
  accepted: "รับแล้ว",
  revoked: "ยกเลิกแล้ว",
  expired: "หมดอายุ",
};

let cachedLabels: AuthzRoleLabelsJson | null = null;
let cachedMv: number | null = null;
let inflight: Promise<AuthzRoleLabelsJson> | null = null;

function mergeLabels(stored: AuthzRoleLabelsJson | null | undefined): AuthzRoleLabelsJson {
  return { ...FALLBACK_ROLE_LABELS, ...(stored ?? {}) };
}

export function peekWorkspaceRoleLabels(): AuthzRoleLabelsJson {
  return cachedLabels ?? FALLBACK_ROLE_LABELS;
}

export async function loadWorkspaceRoleLabels(opts?: {
  force?: boolean;
}): Promise<AuthzRoleLabelsJson> {
  if (!opts?.force && cachedLabels) return cachedLabels;
  if (!opts?.force && inflight) return inflight;

  inflight = (async () => {
    try {
      const raw = (await apiGet(`${API_BASE_URL}/authz/matrix`)) as {
        mv?: number;
        roleLabels?: AuthzRoleLabelsJson;
      };
      const next = mergeLabels(raw?.roleLabels);
      cachedLabels = next;
      cachedMv = typeof raw?.mv === "number" ? raw.mv : null;
      return next;
    } catch {
      const fallback = mergeLabels(null);
      cachedLabels = fallback;
      return fallback;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function workspaceRoleLabelTh(
  role: string | null | undefined,
  labels?: AuthzRoleLabelsJson | null,
): string {
  const key = (role || "").trim();
  if (!key) return "—";
  const map = labels ?? peekWorkspaceRoleLabels();
  return map[key]?.th?.trim() || key;
}

export function workspaceInviteStatusLabelTh(
  status: string | null | undefined,
): string {
  const key = (status || "").trim();
  if (!key) return "—";
  return WORKSPACE_INVITE_STATUS_LABEL_TH[key] ?? key;
}

/** @deprecated use peekWorkspaceRoleLabels / workspaceRoleLabelTh */
export const WORKSPACE_ROLE_LABEL_TH: Record<string, string> = Object.fromEntries(
  Object.entries(FALLBACK_ROLE_LABELS).map(([k, v]) => [k, v.th]),
);

export function getCachedAuthzMv(): number | null {
  return cachedMv;
}
